import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { supabase, clearSupabaseStorage } from '../lib/supabase';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [roleLoading, setRoleLoading] = useState(true)
  const fetchAbortRef = useRef(null)
  const fetchTimeoutRef = useRef(null)

  // Silently sync Stripe subscription for admin users whose status is not yet resolved.
  // Fires on every login/return until subscription_status is a known active state.
  const maybeSyncSubscription = async (profile) => {
    try {
      if (!profile || profile?.role !== 'admin' || !profile?.company_id) return;

      // Check current subscription_status on the company row
      const { data: company } = await supabase
        ?.from('companies')
        ?.select('id, subscription_status')
        ?.eq('id', profile?.company_id)
        ?.maybeSingle();

      if (!company) return;

      // States Stripe has affirmatively told us about — no need to re-sync.
      // Mirrors READ_ONLY_STATUSES + truly-active in Routes.jsx. paused added
      // for B9UB0M58 no-card trial; canceled added so cancelled customers
      // don't trigger a pointless sync on every login/return.
      const resolvedStatuses = ['trialing', 'active', 'past_due', 'unpaid', 'canceled', 'paused'];
      if (resolvedStatuses?.includes(company?.subscription_status)) return;

      // Status is not yet resolved — call sync-subscription edge function
      const { data: { session } } = await supabase?.auth?.getSession();
      const token = session?.access_token;
      if (!token) return;

      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      const anonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/sync-subscription`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'apikey': anonKey,
          },
          body: JSON.stringify({ companyId: company?.id }),
        }
      );

      if (!response?.ok) return;

      const result = await response?.json()?.catch(() => ({}));
      console.log('[AuthContext] Auto subscription sync result:', result?.status);
    } catch (err) {
      // Silent — never block the auth flow
      console.warn('[AuthContext] Auto subscription sync failed silently:', err?.message);
    }
  };

  const fetchProfile = async (userId) => {
    if (!userId) {
      setUserProfile(null)
      setRoleLoading(false)
      return
    }

    // Cancel any pending debounced fetch
    if (fetchTimeoutRef?.current) {
      clearTimeout(fetchTimeoutRef?.current)
      fetchTimeoutRef.current = null
    }

    // Mark previous in-flight fetch as cancelled
    const cancelToken = {}
    fetchAbortRef.current = cancelToken

    // Hard 10s safety timeout — roleLoading must ALWAYS resolve
    const safetyTimer = setTimeout(() => {
      if (fetchAbortRef?.current === cancelToken) {
        fetchAbortRef.current = null
        setRoleLoading(false)
      }
    }, 10000)

    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('*')
        ?.eq('id', userId)
        ?.maybeSingle()

      // If a newer fetch has started, discard this result
      if (fetchAbortRef?.current !== cancelToken) return

      if (!error && data) {
        setUserProfile(data)
        if (data?.preferred_language) {
          // Only apply the profile's stored language if the user has NOT already
          // selected a language on the landing page (or anywhere else). The landing
          // page selection — written to localStorage by LanguageContext/setLanguage —
          // must always take priority over the demo/auth profile value.
          const existingLang = localStorage.getItem('preferredLanguage')
          if (!existingLang) {
            localStorage.setItem('preferredLanguage', data?.preferred_language)
          }
        }
        // Fire-and-forget subscription sync — does not block profile load
        maybeSyncSubscription(data);
      } else {
        setUserProfile(null)
      }
    } catch (err) {
      if (fetchAbortRef?.current !== cancelToken) return
      setUserProfile(null)
    } finally {
      clearTimeout(safetyTimer)
      if (fetchAbortRef?.current === cancelToken) {
        setRoleLoading(false)
      }
    }
  }

  const isInvalidTokenError = (msg = '') => {
    const lower = msg?.toLowerCase()
    return (lower?.includes('refresh token') ||lower?.includes('invalid') ||lower?.includes('not found') ||lower?.includes('token not found') ||lower?.includes('token has expired') || lower?.includes('invalid refresh token'));
  }

  useEffect(() => {
    let initialFetchDone = false

    // Initial session check
    supabase?.auth?.getSession()?.then(({ data: { session }, error }) => {
      // If refresh token is invalid/expired, clear stale storage and treat as logged out
      if (error) {
        if (isInvalidTokenError(error?.message)) {
          clearSupabaseStorage()
        }
        setUser(null)
        setUserProfile(null)
        setLoading(false)
        setRoleLoading(false)
        return
      }
      const currentUser = session?.user ?? null
      setUser(currentUser)
      setLoading(false)
      if (currentUser) {
        initialFetchDone = true
        fetchProfile(currentUser?.id)
      } else {
        setUserProfile(null)
        setRoleLoading(false)
      }
    })?.catch((err) => {
      // Network or unexpected error during session retrieval
      if (isInvalidTokenError(err?.message)) {
        clearSupabaseStorage()
      }
      setUser(null)
      setUserProfile(null)
      setLoading(false)
      setRoleLoading(false)
    })

    // Auth state change listener
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      (event, session) => {
        // Handle token refresh failure — Supabase fires SIGNED_OUT when refresh token is invalid
        if (event === 'SIGNED_OUT' || (event === 'TOKEN_REFRESHED' && !session)) {
          clearSupabaseStorage()
          setUser(null)
          setUserProfile(null)
          setLoading(false)
          setRoleLoading(false)
          return
        }
        const currentUser = session?.user ?? null
        setUser(currentUser)
        setLoading(false)
        if (currentUser) {
          // TOKEN_REFRESHED: the user identity hasn't changed — only the access token
          // was silently refreshed. Do NOT set roleLoading=true or re-fetch the profile.
          // Re-fetching would cause AuthRouter to think it needs to re-resolve, which
          // can briefly read a null company and route an onboarded user back to setup flow.
          if (event === 'TOKEN_REFRESHED') {
            // Token silently refreshed — user and profile are unchanged, nothing to do.
            return
          }
          setRoleLoading(true)
          // Debounce: skip if getSession already triggered a fetch for the same user
          if (event === 'INITIAL_SESSION' && initialFetchDone) return
          // Delay to avoid concurrent lock conflict with getSession
          fetchTimeoutRef.current = setTimeout(() => {
            fetchProfile(currentUser?.id)
          }, 50)
        } else {
          setUserProfile(null)
          setRoleLoading(false)
        }
      }
    )

    return () => {
      subscription?.unsubscribe()
      if (fetchTimeoutRef?.current) clearTimeout(fetchTimeoutRef?.current)
      // Invalidate any in-flight fetch
      fetchAbortRef.current = null
    };
  }, [])

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    try {
      // Clear storage before signing out to prevent stale token errors
      clearSupabaseStorage()
      const { error } = await supabase?.auth?.signOut()
      if (!error) {
        setUser(null)
        setUserProfile(null)
        setRoleLoading(false)
      }
      return { error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.update(updates)
        ?.eq('id', user?.id)
        ?.select()
        ?.maybeSingle()
      if (!error) setUserProfile(data)
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const value = {
    user,
    userProfile,
    loading,
    roleLoading,
    role: userProfile?.role ?? null,
    signIn,
    signOut,
    updateProfile,
    isAuthenticated: !!user,
    isDemoAdmin: !!user && user?.email === import.meta.env?.VITE_DEMO_ADMIN_EMAIL,
    isDemoUser: !!user && [
      import.meta.env?.VITE_DEMO_ADMIN_EMAIL,
      import.meta.env?.VITE_DEMO_MANAGER_EMAIL,
      import.meta.env?.VITE_DEMO_WORKER_EMAIL,
      import.meta.env?.VITE_DEMO_CLIENT_EMAIL,
    ].filter(Boolean).includes(user?.email)
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
